/**
 * Nomenclature picker modal для документів реалізації (2026-06-02).
 *
 * Власник 2026-06-02: «в першій колонці замість випадашки зробити кнопку
 * додати — при кліку на неї з'являється попап там поля фільтр по складам,
 * фільтр за буквами, в таблиці товару колонки назва — одиниця виміру —
 * доступна кількість».
 *
 * Використовується у GoodsSaleEditor (Реалізація): кнопка «+ Додати товар»
 * у першій колонці → відкриває цей попап → бухгалтер бачить актуальні
 * залишки на дату документа + може фільтрувати по складах і назві.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { refs } from '@/api/refs';
import { reports, type GoodsBalanceRow } from '@/api/documents';
import { nomenclature as nomApi } from '@/api/catalogs';


interface Props {
  /** Організація — обов'язково (для запиту залишків). */
  organizationId: string;
  /** Дата документа (для on_date залишків). */
  documentDate: string;
  /** Початковий warehouse (опц.) — використовується як default фільтр. */
  defaultWarehouseId?: string | null;
  /** Колбек при виборі товару. */
  onPick: (row: GoodsBalanceRow) => void;
  /** Закриття попапу. */
  onClose: () => void;
  /**
   * Режим catalog (2026-06-02): показуємо ВСI номенклатури, без перевірки
   * залишків. Корисно для Receipts (купівля від постачальника — товар
   * тільки прибуває, залишків може й не бути). За замовч false — sale mode.
   */
  catalogMode?: boolean;
}

export default function NomenclaturePickerModal({
  organizationId,
  documentDate,
  defaultWarehouseId,
  onPick,
  onClose,
  catalogMode = false,
}: Props) {
  const [warehouseId, setWarehouseId] = useState<string>(defaultWarehouseId ?? '');
  const [search, setSearch] = useState('');

  const warehousesQ = useQuery({
    queryKey: ['warehouses-for-picker', organizationId],
    queryFn: () => refs.warehouses(organizationId),
    enabled: !!organizationId,
  });
  // 2026-06-02: Nomenclature з base_uom_id + UOM list для display.
  const nomListQ = useQuery({
    queryKey: ['nomenclature-for-picker'],
    queryFn: () => nomApi.list({ limit: 1000 }),
  });
  const uomsQ = useQuery({
    queryKey: ['uoms-for-picker'],
    queryFn: refs.uoms,
  });
  // Карта nomenclature_id → uom_short_name
  const uomShortByNomId = useMemo(() => {
    const m = new Map<string, string>();
    const uomById = new Map<string, string>();
    for (const u of uomsQ.data ?? []) {
      uomById.set(u.id, u.short_name);
    }
    for (const n of nomListQ.data?.items ?? []) {
      if (n.base_uom_id) {
        const short = uomById.get(n.base_uom_id);
        if (short) m.set(n.id, short);
      }
    }
    return m;
  }, [nomListQ.data, uomsQ.data]);

  // Балансы на дату документа (только в sale mode — в catalog mode не нужны).
  const balancesQ = useQuery({
    queryKey: ['goods-balance', organizationId, documentDate, warehouseId],
    queryFn: () =>
      reports.goodsBalance({
        organization_id: organizationId,
        on_date: documentDate,
        warehouse_id: warehouseId || undefined,
      }),
    enabled: !catalogMode && !!organizationId && !!documentDate,
  });

  // У catalog mode будуємо рядки з nomenclature (без warehouse + quantity).
  const catalogRows = useMemo<GoodsBalanceRow[]>(() => {
    if (!catalogMode) return [];
    return (nomListQ.data?.items ?? [])
      .filter((n) => !n.deletion_mark)
      .map((n) => ({
        warehouse_id: '',
        warehouse_code: '',
        warehouse_name: '',
        nomenclature_id: n.id,
        nomenclature_code: n.code,
        nomenclature_article: n.article,
        nomenclature_name: n.name,
        quantity: '0',
        cost: null,
      }));
  }, [catalogMode, nomListQ.data]);

  const filteredRows = useMemo(() => {
    const rows = catalogMode ? catalogRows : (balancesQ.data?.rows ?? []);
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      r.nomenclature_name.toLowerCase().includes(q) ||
      (r.nomenclature_code ?? '').toLowerCase().includes(q) ||
      (r.nomenclature_article ?? '').toLowerCase().includes(q),
    );
  }, [balancesQ.data, search]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '90%', maxWidth: 900, maxHeight: '85vh',
          background: 'white', borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1rem',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h3 style={{ margin: 0 }}>Додати товар</h3>
          <button onClick={onClose} style={{ padding: '0.3rem 0.6rem' }}>×</button>
        </div>

        {/* Filters */}
        <div style={{
          padding: '0.75rem 1rem',
          borderBottom: '1px solid var(--border)',
          display: 'grid',
          gridTemplateColumns: catalogMode ? '1fr' : '1fr 2fr',
          gap: '0.75rem',
        }}>
          {!catalogMode && (
            <div>
              <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>
                Склад
              </label>
              <select
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">— Усі склади —</option>
                {warehousesQ.data?.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>
              Пошук (назва, код, артикул)
            </label>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Почніть набирати назву товару…"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        {/* Body — list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem 0' }}>
          {(catalogMode ? nomListQ.isLoading : balancesQ.isLoading) ? (
            <div style={{ padding: '1rem', color: 'var(--muted)' }}>
              {catalogMode ? 'Завантаження довідника…' : 'Завантаження залишків…'}
            </div>
          ) : filteredRows.length === 0 ? (
            <div style={{ padding: '1rem', color: 'var(--muted)' }}>
              {search
                ? 'Нічого не знайдено за вашим запитом.'
                : catalogMode
                ? 'Довідник номенклатури порожній.'
                : (warehouseId
                    ? 'На цьому складі немає товарів з залишками.'
                    : 'Залишки відсутні. Можливо ще не було Надходжень.')}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left', position: 'sticky', top: 0 }}>
                  <th style={{ padding: '0.5rem', width: 100 }}>Код</th>
                  <th style={{ padding: '0.5rem' }}>Назва</th>
                  <th style={{ padding: '0.5rem', width: 80, textAlign: 'center' }}>Од.</th>
                  {!catalogMode && !warehouseId && <th style={{ padding: '0.5rem', width: 160 }}>Склад</th>}
                  {!catalogMode && (
                    <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>
                      Доступна к-ть
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r, i) => (
                  <tr
                    key={`${r.warehouse_id}-${r.nomenclature_id}-${i}`}
                    onClick={() => onPick(r)}
                    style={{
                      cursor: 'pointer',
                      borderTop: '1px solid var(--border-soft)',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-soft)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                  >
                    <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {r.nomenclature_code ?? '—'}
                    </td>
                    <td style={{ padding: '0.5rem' }}>{r.nomenclature_name}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'center', color: 'var(--muted)' }}>
                      {uomShortByNomId.get(r.nomenclature_id) ?? '—'}
                    </td>
                    {!catalogMode && !warehouseId && (
                      <td style={{ padding: '0.5rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
                        {r.warehouse_name}
                      </td>
                    )}
                    {!catalogMode && (
                      <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                        {Number(r.quantity).toLocaleString('uk-UA', { maximumFractionDigits: 3 })}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '0.6rem 1rem',
          borderTop: '1px solid var(--border)',
          fontSize: '0.85rem',
          color: 'var(--muted)',
        }}>
          {filteredRows.length > 0 && (() => {
            const total = catalogMode
              ? (catalogRows.length)
              : (balancesQ.data?.rows.length ?? 0);
            return (
              <span>
                Знайдено: <strong>{filteredRows.length}</strong>
                {filteredRows.length !== total && <span> з {total}</span>}
                {' • '}Натисніть на рядок щоб обрати
              </span>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
